// Small helpers used across App.jsx after the Supabase swap.
//
// newId(): generate a fresh uuid for new records.
//   Replaces the old "V-" + Date.now() + Math.random() pattern. The DB
//   columns are uuid, and Supabase rejects non-uuid id values on upsert.
//
// displayNameForUserId(idOrName, users): map a profiles.id uuid to its
//   display_name using the loaded `users` array. Falls through unchanged if
//   the input doesn't look like a uuid (legacy data that already stored a
//   display name string; nothing to translate).

export function newId() {
  // crypto.randomUUID is available in all modern browsers (Chromium, Firefox,
  // Safari) and in Node 19+. Vite's build target is modern by default.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback — non-cryptographic but valid uuid v4 shape. Should never
  // trigger in production; here as belt-and-braces.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (v) => typeof v === "string" && UUID_RE.test(v);

export function displayNameForUserId(idOrName, users) {
  if (!idOrName) return "";
  if (!isUuid(idOrName)) return idOrName; // already a display name (legacy)
  const match = (users || []).find((u) => u.id === idOrName);
  return match ? (match.displayName || match.display_name || match.email || idOrName) : idOrName;
}

// Reverse — display name back to uuid, for write paths (e.g. assignment dropdown).
// Returns null if not found (caller should handle).
export function userIdForDisplayName(name, users) {
  if (!name) return null;
  if (isUuid(name)) return name;
  const match = (users || []).find(
    (u) => (u.displayName || u.display_name) === name
  );
  return match ? match.id : null;
}
